import Loader from "@/components/loader";
import RouteError from "@/components/route-error";
import { RootLayout } from "@/router-components";
import api from "@/services/api";
import { recordError } from "@/services/record-error";
import {
  adminGuestLoader,
  customerGuestLoader,
  ensureSession,
  publicLayoutLoader,
  requireAdminLoader,
  requireCustomerLoader,
  requireTherapistLoader,
  therapistConfirmationLoader,
  therapistEntryLoader,
  therapistGuestLoader,
  therapistRegistrationLoader,
} from "@/services/router-session";
import {
  buildPublicBookingPath,
  buildPublicTherapistPath,
} from "@/utils/public-therapist-path";

import { lazy } from "react";
import {
  Navigate,
  Route,
  createRoutesFromElements,
  redirect,
} from "react-router";

const CustomerLayout = lazy(() => import("@/layouts/customer-layout"));
const PremiumLayout = lazy(() => import("@/layouts/premium-layout"));
// The public content pages are rendered on the server (app/src/entry-server.jsx)
// and hydrated in the browser: their modules are imported once before
// hydration so the lazy components resolve without replacing the server HTML.
const importPublicLayout = () => import("@/layouts/public-layout");
const importPublicContentList = () => import("@/scenes/public/content");
const importEditorialMethodology = () =>
  import("@/scenes/public/content/methodology");
const importPublicContentView = () => import("@/scenes/public/content/view");

const PublicLayout = lazy(importPublicLayout);
const TherapistLayout = lazy(() => import("@/layouts/therapist-layout"));
const Account = lazy(async () => ({
  default: (await import("@/scenes/admin")).Account,
}));
const AdminBookingList = lazy(async () => ({
  default: (await import("@/scenes/admin")).AdminBookingList,
}));
const AdminBookingView = lazy(async () => ({
  default: (await import("@/scenes/admin")).AdminBookingView,
}));
const AdminContentEdit = lazy(async () => ({
  default: (await import("@/scenes/admin")).AdminContentEdit,
}));
const AdminContentList = lazy(async () => ({
  default: (await import("@/scenes/admin")).AdminContentList,
}));
const AdminCustomerList = lazy(async () => ({
  default: (await import("@/scenes/admin")).AdminCustomerList,
}));
const AdminCustomerView = lazy(async () => ({
  default: (await import("@/scenes/admin")).AdminCustomerView,
}));
const AdminLogin = lazy(async () => ({
  default: (await import("@/scenes/admin")).AdminLogin,
}));
const AdminShell = lazy(async () => ({
  default: (await import("@/scenes/admin")).AdminShell,
}));
const AdminSignup = lazy(async () => ({
  default: (await import("@/scenes/admin")).AdminSignup,
}));
const AdminTherapistList = lazy(async () => ({
  default: (await import("@/scenes/admin")).AdminTherapistList,
}));
const AdminTherapistView = lazy(async () => ({
  default: (await import("@/scenes/admin")).AdminTherapistView,
}));
const AiPrompts = lazy(async () => ({
  default: (await import("@/scenes/admin")).AiPrompts,
}));
const UnsuccessfulSearch = lazy(
  () => import("@/scenes/admin/unsuccessful-search")
);
const Communication = lazy(async () => ({
  default: (await import("@/scenes/admin")).Communication,
}));
const CustomerModificationRequests = lazy(async () => ({
  default: (await import("@/scenes/admin")).CustomerModificationRequests,
}));
const CustomerReviews = lazy(async () => ({
  default: (await import("@/scenes/admin")).CustomerReviews,
}));
const Dashboard = lazy(async () => ({
  default: (await import("@/scenes/admin")).Dashboard,
}));
const EditorialCockpit = lazy(async () => ({
  default: (await import("@/scenes/admin")).EditorialCockpit,
}));
const List = lazy(async () => ({
  default: (await import("@/scenes/admin")).List,
}));
const ReferralSettings = lazy(async () => ({
  default: (await import("@/scenes/admin")).ReferralSettings,
}));
const ClaimCustomerAccount = lazy(async () => ({
  default: (await import("@/scenes/auth")).ClaimCustomerAccount,
}));
const Forgot = lazy(async () => ({
  default: (await import("@/scenes/auth")).Forgot,
}));
const Reset = lazy(async () => ({
  default: (await import("@/scenes/auth")).Reset,
}));
const Signin = lazy(async () => ({
  default: (await import("@/scenes/auth")).Signin,
}));
const TherapistConfirmation = lazy(async () => ({
  default: (await import("@/scenes/auth")).TherapistConfirmation,
}));
const TherapistForgot = lazy(async () => ({
  default: (await import("@/scenes/auth")).TherapistForgot,
}));
const TherapistLanding = lazy(async () => ({
  default: (await import("@/scenes/auth")).TherapistLanding,
}));
const TherapistLogin = lazy(async () => ({
  default: (await import("@/scenes/auth")).TherapistLogin,
}));
const TherapistReset = lazy(async () => ({
  default: (await import("@/scenes/auth")).TherapistReset,
}));
const TherapistStep1 = lazy(async () => ({
  default: (await import("@/scenes/auth")).TherapistStep1,
}));
const TherapistStep2 = lazy(async () => ({
  default: (await import("@/scenes/auth")).TherapistStep2,
}));
const Booking = lazy(() => import("@/scenes/booking"));
const AccountCustomer = lazy(async () => ({
  default: (await import("@/scenes/customer")).AccountCustomer,
}));
const CustomerBookings = lazy(async () => ({
  default: (await import("@/scenes/customer")).CustomerBookings,
}));
const Home = lazy(() => import("@/scenes/home"));
const Contact = lazy(() => import("@/scenes/home/contact"));
const MentionsLegales = lazy(() => import("@/scenes/home/mentions-legales"));
const Privacy = lazy(() => import("@/scenes/home/privacy"));
const PrivacyFr = lazy(() => import("@/scenes/home/privacy-fr"));
const Terms = lazy(() => import("@/scenes/home/terms"));
const CharteDeontologie = lazy(
  () => import("@/scenes/home/charte-deontologie")
);
const PublicContentList = lazy(importPublicContentList);
const EditorialMethodology = lazy(importEditorialMethodology);
const PublicContentView = lazy(importPublicContentView);
const GuidedSearch = lazy(() => import("@/scenes/public/guided-search"));
const GuidedSearchResults = lazy(
  () => import("@/scenes/public/guided-search/results")
);
const PublicInvoiceView = lazy(() => import("@/scenes/public/invoice"));
const PublicSearch = lazy(() => import("@/scenes/public/search"));
const LocalAccompaniment = lazy(
  () => import("@/scenes/public/accompagnements")
);
const Agenda = lazy(async () => ({
  default: (await import("@/scenes/therapist")).Agenda,
}));
const ClientView = lazy(async () => ({
  default: (await import("@/scenes/therapist")).ClientView,
}));
const DashboardSettings = lazy(async () => ({
  default: (await import("@/scenes/therapist")).DashboardSettings,
}));
const Help = lazy(async () => ({
  default: (await import("@/scenes/therapist")).Help,
}));
const InformationMessages = lazy(async () => ({
  default: (await import("@/scenes/therapist")).InformationMessages,
}));
const InvoiceDashboard = lazy(async () => ({
  default: (await import("@/scenes/therapist")).InvoiceDashboard,
}));
const InvoiceList = lazy(async () => ({
  default: (await import("@/scenes/therapist")).InvoiceList,
}));
const InvoiceView = lazy(async () => ({
  default: (await import("@/scenes/therapist")).InvoiceView,
}));
const Offers = lazy(async () => ({
  default: (await import("@/scenes/therapist")).Offers,
}));
const Offices = lazy(async () => ({
  default: (await import("@/scenes/therapist")).Offices,
}));
const PaymentsList = lazy(async () => ({
  default: (await import("@/scenes/therapist")).PaymentsList,
}));
const ProfileEdit = lazy(async () => ({
  default: (await import("@/scenes/therapist")).ProfileEdit,
}));
const Referral = lazy(async () => ({
  default: (await import("@/scenes/therapist")).Referral,
}));
const Settings = lazy(async () => ({
  default: (await import("@/scenes/therapist")).Settings,
}));
const Subscription = lazy(async () => ({
  default: (await import("@/scenes/therapist")).Subscription,
}));
const TherapistCustomersLayout = lazy(async () => ({
  default: (await import("@/scenes/therapist")).TherapistCustomersLayout,
}));
const TherapistCustomersList = lazy(async () => ({
  default: (await import("@/scenes/therapist")).TherapistCustomersList,
}));
const TherapistCustomersView = lazy(async () => ({
  default: (await import("@/scenes/therapist")).TherapistCustomersView,
}));
const TherapistDashboard = lazy(async () => ({
  default: (await import("@/scenes/therapist")).TherapistDashboard,
}));
const TherapistPublicRedirect = lazy(async () => ({
  default: (await import("@/scenes/therapist")).TherapistPublicRedirect,
}));
const TherapistPublicProfileView = lazy(
  () => import("@/scenes/therapist/profile/view")
);

async function legacySpecialityLoader({ params }) {
  return redirect(`/search/${params.specialitySlug || ""}`);
}

async function legacyPractitionerLoader({ params }) {
  if (!params.profileSlug) {
    return redirect("/therapists");
  }

  try {
    const response = await api.get(
      `/public-therapist/slug/${params.profileSlug}`
    );
    if (!response?.ok || !response?.data) {
      return redirect("/therapists");
    }

    const canonicalPath =
      response?.meta?.canonical_path || buildPublicTherapistPath(response.data);

    if (!canonicalPath) {
      return redirect("/therapists");
    }

    return redirect(canonicalPath);
  } catch (error) {
    recordError(error, {
      source: "router",
      operation: "legacyPractitionerLoader",
    });
    return redirect("/therapists");
  }
}

async function resolveBookingAccessRedirect(bookingId) {
  const customer = await ensureSession("customer");

  try {
    const { ok, exists, data } = await api.get(`/booking/access/${bookingId}`);

    if (!ok || !exists) {
      return redirect("/");
    }

    if (!customer?._id) {
      if (data?.customer_status === "unclaimed") {
        return redirect(
          `/auth?activation=claim-required&booking_id=${encodeURIComponent(
            bookingId
          )}&redirect=${encodeURIComponent(`/customer/bookings/${bookingId}`)}`
        );
      }

      return redirect(
        `/auth?redirect=${encodeURIComponent(`/customer/bookings/${bookingId}`)}`
      );
    }

    return redirect(`/customer/bookings/${bookingId}`);
  } catch (error) {
    recordError(error, {
      source: "router",
      operation: "resolveBookingAccessRedirect",
    });
    return redirect("/");
  }
}

export async function publicBookingLoader({ params }) {
  const publicBookingParam = params.therapistId;

  if (!publicBookingParam) {
    return redirect("/");
  }

  try {
    const response = await api.get(`/public-therapist/${publicBookingParam}`, {
      capture: false,
    });
    if (response?.ok && response?.data?._id) {
      if (response.data.plan === "free") {
        return redirect(buildPublicTherapistPath(response.data));
      }
      return {
        therapist: response.data,
        meta: response?.meta || null,
      };
    }
  } catch (error) {
    recordError(error, {
      source: "router",
      operation: "publicBookingLoader",
      expected: Number(error?.status) === 404,
    });
    // Fallback to booking access redirect below.
  }

  // `/booking/:therapistId` is also used by booking-email links.
  // If the param is not a public therapist id, treat it as a booking access id.
  return resolveBookingAccessRedirect(publicBookingParam);
}

export async function publicBookingBySlugLoader({ params, request }) {
  if (!params.profileSlug) return redirect("/therapists");

  try {
    const response = await api.get(
      `/public-therapist/slug/${params.profileSlug}`
    );
    if (!response?.ok || !response?.data?._id) {
      return redirect("/therapists");
    }

    if (response.data.plan === "free") {
      return redirect(
        `${response?.meta?.canonical_path || buildPublicTherapistPath(response.data)}${new URL(request.url).search}`
      );
    }

    const canonicalPath = buildPublicBookingPath(
      response.data,
      response?.meta?.canonical_path
    );
    const currentUrl = new URL(request.url);
    if (canonicalPath && currentUrl.pathname !== canonicalPath) {
      return redirect(`${canonicalPath}${currentUrl.search}`);
    }

    return {
      therapist: response.data,
      meta: response?.meta || null,
    };
  } catch (error) {
    recordError(error, {
      source: "router",
      operation: "publicBookingBySlugLoader",
      expected: Number(error?.status) === 404,
    });
    return redirect("/therapists");
  }
}

export async function requireCustomerAreaLoader({ request }) {
  const bookingMatch = new URL(request.url).pathname.match(
    /^\/customer\/bookings\/([^/]+)$/
  );

  if (!bookingMatch) return requireCustomerLoader({ request });

  const customer = await ensureSession("customer");
  if (customer?._id) return null;

  return resolveBookingAccessRedirect(decodeURIComponent(bookingMatch[1]));
}

// Loader data of a server-rendered content page: the only loader on its
// route branch is the public layout session loader, marked as already run
// on both sides so neither the static router nor the browser router renders
// the hydrate fallback. PublicLayout re-runs it in an effect.
export const CONTENT_PAGE_LOADER_DATA = { "public-layout": null };

export async function preloadPublicContentRoute(pathname) {
  await importPublicLayout();
  if (/^\/methodologie-editoriale\/?$/.test(pathname)) {
    await importEditorialMethodology();
    return;
  }
  if (/^\/(guides|ressources-pro)\/[^/]+\/?$/.test(pathname)) {
    await importPublicContentView();
    return;
  }
  await importPublicContentList();
}

export const routes = createRoutesFromElements(
  <Route
    path="/"
    shouldRevalidate={() => false}
    element={<RootLayout />}
    hydrateFallbackElement={<Loader />}
    errorElement={<RouteError />}
  >
    <Route path="login" loader={() => redirect("/auth")} />
    <Route path="signup" loader={() => redirect("/auth?step=signup")} />
    <Route path="forgot-password" loader={() => redirect("/auth/forgot")} />

    <Route path="auth" errorElement={<RouteError />}>
      <Route index element={<Signin />} loader={customerGuestLoader} />
      <Route path="register" loader={() => redirect("/auth")} />
      <Route path="login" loader={() => redirect("/auth")} />
      <Route path="forgot" element={<Forgot />} />
      <Route path="reset" element={<Reset />} />
      <Route path="claim-customer-account" element={<ClaimCustomerAccount />} />
      <Route path="therapist" loader={() => redirect("/therapist")} />
      <Route
        path="therapist/register"
        element={<TherapistStep1 />}
        loader={therapistGuestLoader}
      />
      <Route
        path="therapist/register/complete"
        element={<TherapistStep2 />}
        loader={therapistRegistrationLoader}
      />
      <Route
        path="therapist/confirmation"
        element={<TherapistConfirmation />}
        loader={therapistConfirmationLoader}
      />
      <Route
        path="therapist/pending"
        loader={() => redirect("/auth/therapist/confirmation")}
      />
      <Route
        path="therapist/login"
        element={<TherapistLogin />}
        loader={therapistGuestLoader}
      />
      <Route
        path="therapist/forgot"
        element={<TherapistForgot />}
        loader={therapistGuestLoader}
      />
      <Route
        path="therapist/reset"
        element={<TherapistReset />}
        loader={therapistGuestLoader}
      />
    </Route>

    <Route
      id="public-layout"
      element={<PublicLayout />}
      loader={publicLayoutLoader}
      shouldRevalidate={() => false}
      errorElement={<RouteError />}
    >
      <Route index element={<Home />} />
      <Route path="guided-search" element={<GuidedSearch />} />
      <Route path="guided-search/results" element={<GuidedSearchResults />} />
      <Route path="guides" element={<PublicContentList />} />
      <Route path="guides/:slug" element={<PublicContentView />} />
      <Route
        path="methodologie-editoriale"
        element={<EditorialMethodology />}
      />
      <Route
        path="ressources-pro"
        element={<PublicContentList audience="pro" />}
      />
      <Route
        path="ressources-pro/:slug"
        element={<PublicContentView audience="pro" />}
      />
      <Route path="therapists" element={<PublicSearch />} />
      <Route
        path="accompagnements/hypnose-troyes"
        element={<LocalAccompaniment />}
        loader={async () => {
          const response = await api.post("/public-therapist/search", {
            local_page: "hypnose-troyes",
          });
          if (!response.ok) throw new Error(response.code);
          return response.data;
        }}
      />
      <Route path="therapists/:id" element={<TherapistPublicProfileView />} />
      <Route path="invoice/shared/:token" element={<PublicInvoiceView />} />
      <Route
        path="therapeutes/:specialitySlug/:citySlug/:profileSlug"
        element={<TherapistPublicProfileView />}
      />
      <Route
        path="therapeutes/:specialitySlug/:citySlug/:profileSlug/prendre-rendez-vous"
        element={<Booking />}
        loader={publicBookingBySlugLoader}
      />
      <Route path="search" element={<PublicSearch />} />
      <Route path="public-therapists" loader={() => redirect("/therapists")} />
      <Route
        path="therapist"
        element={<TherapistLanding />}
        loader={therapistEntryLoader}
      />
      <Route path="mentions-legales" element={<MentionsLegales />} />
      <Route path="contact" element={<Contact />} />
      <Route path="privacy" element={<Privacy />} />
      <Route path="privacy-fr" element={<PrivacyFr />} />
      <Route path="terms" element={<Terms />} />
      <Route path="charte-deontologie" element={<CharteDeontologie />} />
      <Route path="search/:specialitySlug" element={<PublicSearch />} />
      <Route
        path="booking/:therapistId"
        element={<Booking />}
        loader={publicBookingLoader}
      />
      <Route
        path="search/:specialitySlug/:departmentSlug"
        element={<PublicSearch />}
      />
      <Route
        path="search/:specialitySlug/:departmentSlug/:citySlug"
        element={<PublicSearch />}
      />
      <Route path="praticien/:profileSlug" loader={legacyPractitionerLoader} />
      <Route path=":specialitySlug" loader={legacySpecialityLoader} />
    </Route>

    <Route
      path="customer"
      element={<CustomerLayout />}
      loader={requireCustomerAreaLoader}
      errorElement={<RouteError />}
    >
      <Route index element={<Home />} />
      <Route path="bookings/:id?" element={<CustomerBookings />} />
      <Route path="account" element={<AccountCustomer />} />
    </Route>

    <Route
      element={<TherapistLayout />}
      loader={requireTherapistLoader}
      errorElement={<RouteError />}
    >
      <Route path="therapist/agenda" element={<Agenda />} />
      <Route path="therapist/client-view" element={<ClientView />} />
      <Route element={<PremiumLayout />}>
        <Route path="therapist/dashboard" element={<TherapistDashboard />} />
        <Route
          path="therapist/dashboard/settings"
          element={<DashboardSettings />}
        />
      </Route>
      <Route path="therapist/customers" element={<TherapistCustomersLayout />}>
        <Route index element={<TherapistCustomersList />} />
        <Route path=":id" element={<TherapistCustomersView />} />
      </Route>
      <Route
        path="therapist/information-messages"
        element={<InformationMessages />}
      />
      <Route path="therapist/offices" element={<Offices />} />
      <Route path="therapist/offers/:tab?" element={<Offers />} />
      <Route path="therapist/payments" element={<PremiumLayout />}>
        <Route index element={<PaymentsList />} />
      </Route>
      <Route path="therapist/invoice" element={<PremiumLayout />}>
        <Route index element={<InvoiceDashboard />} />
        <Route path="list" element={<InvoiceList />} />
        <Route
          path="pending"
          element={
            <Navigate to="/therapist/invoice/list?status=pending" replace />
          }
        />
        <Route path=":id" element={<InvoiceView />} />
      </Route>
      <Route path="therapist/settings" element={<Settings />} />
      <Route path="therapist/help" element={<Help />} />
      <Route path="therapist/referral" element={<Referral />} />
      <Route path="therapist/subscription" element={<Subscription />} />
      <Route path="therapist/account" element={<ProfileEdit />} />
      <Route
        path="therapist/account/public"
        element={<TherapistPublicRedirect />}
      />
    </Route>

    <Route path="admin" element={<AdminShell />} errorElement={<RouteError />}>
      <Route
        path="auth/login"
        element={<AdminLogin />}
        loader={adminGuestLoader}
      />
      <Route
        path="auth/signup"
        element={<AdminSignup />}
        loader={adminGuestLoader}
      />
      <Route loader={requireAdminLoader}>
        <Route element={<List />}>
          <Route index element={<Dashboard />} />
          <Route path="therapist">
            <Route index element={<AdminTherapistList />} />
            <Route path=":id" element={<AdminTherapistView />} />
          </Route>
          <Route path="customer">
            <Route index element={<AdminCustomerList />} />
            <Route path=":id" element={<AdminCustomerView />} />
          </Route>
          <Route
            path="customer-modification-requests"
            element={<CustomerModificationRequests />}
          />
          <Route path="customer-reviews" element={<CustomerReviews />} />
          <Route path="booking">
            <Route index element={<AdminBookingList />} />
            <Route path=":id" element={<AdminBookingView />} />
          </Route>
          <Route path="unsuccessful-search" element={<UnsuccessfulSearch />} />
          <Route path="communication" element={<Communication />} />
          <Route path="ai-prompts" element={<AiPrompts />} />
          <Route path="referral-settings" element={<ReferralSettings />} />
          <Route
            path="content-suggestions"
            element={<Navigate to="/admin/editorial" replace />}
          />
          <Route path="editorial" element={<EditorialCockpit />} />
          <Route path="content">
            <Route index element={<AdminContentList />} />
            <Route path="add" element={<AdminContentEdit />} />
            <Route path=":id" element={<AdminContentEdit />} />
          </Route>
          <Route path="account" element={<Account />} />
        </Route>
      </Route>
    </Route>

    <Route path="*" loader={() => redirect("/")} />
  </Route>
);
